import {
  Button,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Tag,
  Tooltip,
  message,
} from "antd";
import { DeleteOutlined, EditOutlined, SwapOutlined } from "@ant-design/icons";
import React, { useState } from "react";
import {
  AiOutlineDatabase,
  AiOutlineInbox,
  AiOutlineStop,
  AiOutlineWarning,
} from "react-icons/ai";
import { useMutation, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/slices/auth.slice";
import CreateGroupStockModal from "../CreateGroupStockModal";
import EditGroupStockModal from "../EditGroupStockModal";
import GroupListScreen from "../GroupListScreen";
import useGroupBranchOptions from "../useGroupBranchOptions";
import {
  adjustGroupStock,
  deleteGroupStock,
  fetchGroupStock,
} from "../../../services/groupConsole.service";

// Group-wide totals across every branch's rows, same math as the branch
// admin's own StockItems.js summary strip (getSummary on the backend).
const buildStats = (rows) => {
  const lowStock = rows.filter(
    (r) => (r.quantity_in_hand ?? 0) <= (r.low_stock_threshold ?? 0),
  );
  const outOfStock = rows.filter((r) => (r.quantity_in_hand ?? 0) <= 0);
  return [
    {
      key: "items",
      label: "Items",
      value: rows.length,
      icon: <AiOutlineInbox size={15} />,
      color: "indigo",
    },
    {
      key: "units",
      label: "Units in hand",
      value: rows.reduce((sum, r) => sum + (r.quantity_in_hand || 0), 0),
      icon: <AiOutlineDatabase size={15} />,
      color: "sky",
    },
    {
      key: "low",
      label: "Low stock",
      value: lowStock.length,
      icon: <AiOutlineWarning size={15} />,
      color: "amber",
    },
    {
      key: "out",
      label: "Out of stock",
      value: outOfStock.length,
      icon: <AiOutlineStop size={15} />,
      color: "rose",
    },
  ];
};

const GroupStock = () => {
  const user = useSelector(selectUser);
  const groupId = user?.group;
  const branches = useGroupBranchOptions();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [adjusting, setAdjusting] = useState(null);
  const [delta, setDelta] = useState(0);
  const [adjustNote, setAdjustNote] = useState("");

  const refresh = () => queryClient.invalidateQueries("group-console-stock");

  const openAdjust = (record) => {
    setAdjusting(record);
    setDelta(0);
    setAdjustNote("");
  };

  const adjustMutation = useMutation(
    () =>
      adjustGroupStock(groupId, adjusting.school, adjusting._id, {
        delta,
        note: adjustNote || undefined,
      }),
    {
      onSuccess: () => {
        message.success(
          `${adjusting.name} ${delta > 0 ? "increased" : "reduced"} by ${Math.abs(
            delta,
          )}`,
        );
        setAdjusting(null);
        refresh();
      },
      onError: (err) => {
        message.error(
          err?.response?.data?.error?.errors?.[0] || "Something went wrong",
        );
      },
    },
  );

  const handleAdjust = () => {
    if (!delta) {
      message.error("Enter a non-zero amount");
      return;
    }
    adjustMutation.mutate();
  };

  const handleDelete = async (record) => {
    try {
      await deleteGroupStock(groupId, record.school, record._id);
      message.success(`${record.name} removed from the catalogue`);
      refresh();
    } catch (err) {
      message.error(
        err?.response?.data?.error?.errors?.[0] || "Something went wrong",
      );
    }
  };

  return (
    <>
      <GroupListScreen
        title="Stock Inventory"
        queryKey="group-console-stock"
        fetchFn={fetchGroupStock}
        searchPlaceholder="Search by item or category"
        branchOptions={branches.options}
        branchOptionsLoading={branches.isLoading}
        statCards={buildStats}
        extraHeader={
          <Button type="primary" onClick={() => setShowCreate(true)}>
            Create Item
          </Button>
        }
        columns={[
          { title: "Item", dataIndex: "name" },
          { title: "Category", dataIndex: "category", align: "center" },
          { title: "In Hand", dataIndex: "quantity_in_hand", align: "center" },
          {
            title: "Low Stock",
            key: "low_stock",
            align: "center",
            render: (r) =>
              (r.quantity_in_hand ?? 0) <= (r.low_stock_threshold ?? 0) ? (
                <Tag color="red">Reorder</Tag>
              ) : (
                <Tag color="green">OK</Tag>
              ),
          },
          {
            title: "Action",
            key: "action",
            width: 150,
            align: "center",
            render: (r) => (
              <div className="flex items-center justify-center gap-3">
                <Tooltip title="Adjust quantity">
                  <Button
                    size="small"
                    shape="circle"
                    icon={
                      <SwapOutlined
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    }
                    onClick={() => openAdjust(r)}
                  />
                </Tooltip>
                <Tooltip title="Edit">
                  <Button
                    size="small"
                    shape="circle"
                    icon={
                      <EditOutlined
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    }
                    onClick={() => setEditing(r)}
                  />
                </Tooltip>
                <Popconfirm
                  title={`Remove ${r.name}?`}
                  description="This can't be undone."
                  okText="Remove"
                  cancelText="Cancel"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => handleDelete(r)}
                >
                  <Tooltip title="Remove">
                    <Button
                      size="small"
                      shape="circle"
                      danger
                      icon={
                        <DeleteOutlined
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        />
                      }
                    />
                  </Tooltip>
                </Popconfirm>
              </div>
            ),
          },
        ]}
      />
      <CreateGroupStockModal
        groupId={groupId}
        open={showCreate}
        onClose={() => setShowCreate(false)}
      />
      <EditGroupStockModal
        groupId={groupId}
        item={editing}
        open={!!editing}
        onClose={() => setEditing(null)}
      />
      <Modal
        title={`Adjust stock — ${adjusting?.name || ""}`}
        open={!!adjusting}
        onCancel={() => setAdjusting(null)}
        onOk={handleAdjust}
        okText="Apply"
        confirmLoading={adjustMutation.isLoading}
        destroyOnClose
      >
        <p className="pb-3 text-gray-600">
          Currently <b>{adjusting?.quantity_in_hand}</b> {adjusting?.unit} in
          hand. Use a positive amount to receive new supply, a negative one to
          correct a miscount.
        </p>
        <InputNumber
          className="w-full"
          precision={0}
          value={delta}
          onChange={(value) => setDelta(value || 0)}
          placeholder="e.g. 25 or -3"
        />
        <Input.TextArea
          style={{ marginTop: 12 }}
          rows={2}
          value={adjustNote}
          onChange={(e) => setAdjustNote(e.target.value)}
          placeholder="Note (optional) — e.g. new supply received from vendor"
        />
        {delta !== 0 && (
          <p className="pt-3 text-gray-600">
            New quantity will be{" "}
            <b>{(adjusting?.quantity_in_hand || 0) + delta}</b>{" "}
            {adjusting?.unit}.
          </p>
        )}
      </Modal>
    </>
  );
};

export default GroupStock;
